import React, { useMemo, useState } from 'react'
import { X } from 'phosphor-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import {
    ModalBackdrop,
    ModalBox,
    ModalHead,
    ModalBody,
    ModalFoot,
    SrcRule,
    QSearch,
    SectGroup,
    SectHead,
    QPick,
    QTag,
    ConflictRow,
} from './styles'
import {
    answerOffersAllTriggers,
    formatAssigneeIcon,
    formatMinutesLabel,
    formatRuleAssigneesLabel,
    formatRuleCustomersLabel,
    formatTriggerValuesLabel,
    getRuleTriggerValues,
    triggersOverlap,
} from './utils'

const CONFLICT_MODES = {
    replace: 'Replace',
    skip: 'Skip',
    keepBoth: 'KeepBoth',
}

export const CopyRuleModal = ({
    open,
    onClose,
    rule,
    questions,
    existingRules,
    onConfirm,
    saving,
}) => {
    const [search, setSearch] = useState('')
    const [selected, setSelected] = useState({})
    const [conflictMode, setConflictMode] = useState('replace')

    const sections = useMemo(() => {
        const map = new Map()
        const list = questions || []
        list.forEach(q => {
            const key = q.sectionName || 'Other'
            if (!map.has(key)) map.set(key, [])
            map.get(key).push(q)
        })
        return [...map.entries()]
    }, [questions])

    const filteredSections = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return sections
        return sections
            .map(([name, qs]) => [
                name,
                qs.filter(item =>
                    item.questionText?.toLowerCase().includes(q),
                ),
            ])
            .filter(([, qs]) => qs.length > 0)
    }, [sections, search])

    const sourceTriggers = getRuleTriggerValues(rule)

    const getQuestionStatus = q => {
        if (Number(q.auditQuestionId) === Number(rule?.auditQuestionId)) {
            return { disabled: true, tag: 'skipped', tagLabel: 'Source rule' }
        }
        if (!answerOffersAllTriggers(q, sourceTriggers)) {
            return {
                disabled: true,
                tag: 'skipped',
                tagLabel: 'Incompatible',
            }
        }
        const existing = (existingRules || []).find(
            r =>
                Number(r.auditQuestionId) === Number(q.auditQuestionId) &&
                r.isEnabled &&
                triggersOverlap(r, rule),
        )
        if (existing) {
            return { disabled: false, tag: 'replace', tagLabel: 'Has different rule' }
        }
        return { disabled: false, tag: 'ok', tagLabel: 'Eligible' }
    }

    const selectedIds = Object.keys(selected).filter(k => selected[k])
    const replaceCount = selectedIds.filter(id => {
        const q = questions.find(x => String(x.auditQuestionId) === id)
        if (!q) return false
        const st = getQuestionStatus(q)
        return st.tag === 'replace'
    }).length

    const toggle = (q, status) => {
        if (status.disabled) return
        const id = String(q.auditQuestionId)
        setSelected(prev => ({ ...prev, [id]: !prev[id] }))
    }

    const selectAllEligible = sectionQuestions => {
        const next = { ...selected }
        sectionQuestions.forEach(q => {
            const st = getQuestionStatus(q)
            if (!st.disabled) next[String(q.auditQuestionId)] = true
        })
        setSelected(next)
    }

    const handleConfirm = () => {
        const ids = selectedIds.map(Number)
        if (!ids.length) return
        onConfirm({
            TargetQuestionIds: ids,
            ConflictMode: CONFLICT_MODES[conflictMode] || 'Replace',
        })
    }

    if (!open || !rule) return null

    const icon = formatAssigneeIcon(
        rule.fixedAssignedToTypeName || rule.assignedToTypeName,
    )

    return (
        <ModalBackdrop onClick={e => e.target === e.currentTarget && onClose()}>
            <ModalBox>
                <ModalHead>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: 'none',
                                border: 0,
                                cursor: 'pointer',
                            }}
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <h2>📋 Apply this rule to other questions</h2>
                    <div className="m-sub">
                        Pick questions in this template that should get the same
                        auto-ticket behaviour.
                    </div>
                </ModalHead>
                <ModalBody>
                    <SrcRule>
                        <div className="src-label">Rule being copied</div>
                        <div className="src-sentence">
                            From question <b>&ldquo;{rule.questionText}&rdquo;</b>{' '}
                            — when answered{' '}
                            <b>{formatTriggerValuesLabel(rule, ' or ')}</b>,
                            create a <b>{rule.priorityName}</b> ticket for{' '}
                            <b>
                                {icon} {formatRuleAssigneesLabel(rule)}
                            </b>
                            , acknowledge in{' '}
                            <b>{formatMinutesLabel(rule.ackSlaMinutes)}</b>,
                            resolve in{' '}
                            <b>{formatMinutesLabel(rule.resolveSlaMinutes)}</b>
                            . Applies to{' '}
                            <b>{formatRuleCustomersLabel(rule)}</b>.
                            {rule.categoryName && (
                                <>
                                    {' '}
                                    Category: <b>{rule.categoryName}</b>.
                                </>
                            )}
                        </div>
                    </SrcRule>
                    <QSearch
                        type="text"
                        placeholder="Search questions in this template…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {filteredSections.map(([sectionName, qs]) => {
                        const eligible = qs.filter(
                            q => !getQuestionStatus(q).disabled,
                        ).length
                        return (
                            <SectGroup key={sectionName}>
                                <SectHead>
                                    <span>{sectionName}</span>
                                    {eligible > 0 && (
                                        <span
                                            className="sect-actions"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() =>
                                                selectAllEligible(qs)
                                            }
                                            onKeyDown={e =>
                                                e.key === 'Enter' &&
                                                selectAllEligible(qs)
                                            }
                                        >
                                            Select all eligible ({eligible})
                                        </span>
                                    )}
                                </SectHead>
                                {qs.map(q => {
                                    const st = getQuestionStatus(q)
                                    const id = String(q.auditQuestionId)
                                    const opts = (
                                        q.multiAnswers?.length
                                            ? q.multiAnswers
                                            : []
                                    )
                                        .map(a => a.DisplayText || a.Response)
                                        .filter(Boolean)
                                        .join(' / ')
                                    return (
                                        <QPick
                                            key={id}
                                            $disabled={st.disabled}
                                            $selected={!!selected[id]}
                                            onClick={() => toggle(q, st)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={!!selected[id]}
                                                disabled={st.disabled}
                                                onChange={() => toggle(q, st)}
                                                onClick={e =>
                                                    e.stopPropagation()
                                                }
                                            />
                                            <div>
                                                <div className="qq">
                                                    {q.questionText}{' '}
                                                    <QTag $type={st.tag}>
                                                        {st.tagLabel}
                                                    </QTag>
                                                </div>
                                                <div className="qq-meta">
                                                    {opts || 'Various answers'}
                                                    {st.tag === 'replace' &&
                                                        ' · existing rule will be replaced'}
                                                </div>
                                            </div>
                                        </QPick>
                                    )
                                })}
                            </SectGroup>
                        )
                    })}
                    <ConflictRow>
                        <div className="ch-label">
                            ⚠ When a question already has a rule
                        </div>
                        <div className="ch-opts">
                            <label>
                                <input
                                    type="radio"
                                    name="conflict"
                                    checked={conflictMode === 'replace'}
                                    onChange={() => setConflictMode('replace')}
                                />
                                Replace the existing rule
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="conflict"
                                    checked={conflictMode === 'skip'}
                                    onChange={() => setConflictMode('skip')}
                                />
                                Keep existing &amp; skip
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="conflict"
                                    checked={conflictMode === 'keepBoth'}
                                    onChange={() => setConflictMode('keepBoth')}
                                />
                                Keep both
                            </label>
                        </div>
                    </ConflictRow>
                </ModalBody>
                <ModalFoot>
                    <div className="count">
                        <b>{selectedIds.length}</b> question(s) selected
                        {replaceCount > 0 && (
                            <span style={{ color: '#92400e' }}>
                                {' '}
                                · {replaceCount} will be replaced
                            </span>
                        )}
                    </div>
                    <div className="btns">
                        <AppSecondaryButton onClick={onClose}>
                            Cancel
                        </AppSecondaryButton>
                        <AppPrimaryButton
                            onClick={handleConfirm}
                            disabled={!selectedIds.length || saving}
                            loading={saving}
                        >
                            Copy rule to selected →
                        </AppPrimaryButton>
                    </div>
                </ModalFoot>
            </ModalBox>
        </ModalBackdrop>
    )
}
